import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId, } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cx } from '../../../internal/cx.js';
import { ChevronRightGlyph } from '../../../internal/icons.js';
import { Spinner } from '../../feedback/spinner/index.js';
/**
 * An unboxed section header: title, optional description and meta line, and
 * trailing actions or a "View all" link. With `children` it becomes the
 * whole labelled `<section>`; without, it is the header row alone (for
 * example inside a Card).
 */
export const SectionHeader = forwardRef(function SectionHeader({ title, description, meta, actions, link, headingLevel: H = 'h2', headingId, size = 'sm', busy = false, busyLabel = 'Loading', className, children, ...rest }, ref) {
    const auto = useId();
    const id = headingId ?? `mn-section-header-${auto}`;
    const hasBody = children !== undefined && children !== null && children !== false;
    const bar = (_jsxs("div", { className: "mn-section-header__bar", children: [_jsxs("div", { className: "mn-section-header__text", children: [_jsxs("div", { className: "mn-section-header__heading", children: [_jsx(H, { id: id, className: "mn-section-header__title", children: title }), busy ? (_jsx(Spinner, { size: "sm", label: busyLabel, className: "mn-section-header__spinner" })) : null] }), description ? _jsx("p", { className: "mn-section-header__description", children: description }) : null, meta ? _jsx("p", { className: "mn-section-header__meta", children: meta }) : null] }), actions || link ? (_jsxs("div", { className: "mn-section-header__trailing", children: [actions, link] })) : null] }));
    const common = {
        ...rest,
        'aria-busy': busy || undefined,
        'data-size': size,
        'data-busy': busy || undefined,
        className: cx('mn-section-header', className),
    };
    if (!hasBody) {
        return (_jsx("div", { ref: ref, ...common, children: bar }));
    }
    return (_jsxs("section", { ref: ref, "aria-labelledby": id, ...common, children: [bar, _jsx("div", { className: "mn-section-header__body", children: children })] }));
});
/**
 * The trailing "View all" link of a SectionHeader, with a decorative arrow.
 * Give it text that makes sense out of context ("View all invoices") or an
 * `aria-label`, since a page often has several.
 */
export const SectionHeaderLink = forwardRef(function SectionHeaderLink({ asChild = false, className, children, ...rest }, ref) {
    const Comp = asChild ? Slot : 'a';
    return (_jsxs(Comp, { ref: ref, ...rest, className: cx('mn-section-header__link', className), children: [_jsx(Slottable, { children: children }), _jsx(ChevronRightGlyph, { className: "mn-section-header__arrow" })] }));
});
//# sourceMappingURL=section-header.js.map